import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { ImageProps, getTokenValue, Token, styled, View } from 'tamagui'

import { Image } from '../Image'

import { UserPrimaryPhotoNew_user$key } from './__generated__/UserPrimaryPhotoNew_user.graphql'

const UserPrimaryPhotoFrame = styled(Image, {
  name: 'UserPrimaryPhoto',
  // @ts-expect-error boxShadow is valid CSS
  boxShadow: '0 0 0 2px #FFFFFF',
  // boxShadow: '0 0 0 16px #FF0000',
  borderColor: 'rgba(19, 0, 102, 0.16)',
  borderWidth: 1,
  backgroundColor: '$grey0',

  variants: {
    thinShadow: {
      true: {
        boxShadow: '0 0 0 1px #FFFFFF',
      },
    },
  },

  defaultVariants: {
    thinShadow: false,
  },
} as const)

export const UserPrimaryPhoto = UserPrimaryPhotoFrame.styleable<{
  user: UserPrimaryPhotoNew_user$key
  size: Token
}>(({ user, size = '$5', ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment UserPrimaryPhotoNew_user on User
      @argumentDefinitions(
        imageSize: { type: "Int", defaultValue: 100 }
        dpr: { type: "Int", defaultValue: 3 }
      ) {
        firstName
        lastName
        rowId
        emailMd5

        primaryPhoto {
          transformUrl(
            transform: {
              w: $imageSize
              h: $imageSize
              dpr: $dpr
              fo: "auto"
              fit: "cover"
            }
          )
        }

        # Signed URL for default avatar when no custom photo is set
        defaultAvatarUrl(
          transform: { w: $imageSize, h: $imageSize, dpr: $dpr, fo: "auto" }
        )
      }
    `,
    user
  )

  const parsedSize = useMemo(() => {
    return getTokenValue(size, 'size') as number
  }, [size])

  if (data.primaryPhoto?.transformUrl) {
    return (
      <UserPrimaryPhotoFrame
        {...props}
        alt={`Primary Photo for ${data.firstName} ${data.lastName}`}
        ref={ref}
        src={data.primaryPhoto.transformUrl}
        width={parsedSize}
        height={parsedSize}
        borderRadius={size}
      />
    )
  }

  const alt = `Primary Photo for ${data.firstName} ${data.lastName}`

  // No primary photo: overlay Gravatar (d=blank) on top of default avatar.
  // d=blank returns a transparent image (200) instead of 404 when no Gravatar exists,
  // so the default avatar shows through. If the user has a Gravatar, it covers the default.
  if (data.emailMd5) {
    return (
      <View position="relative" width={parsedSize} height={parsedSize}>
        <UserPrimaryPhotoFrame
          {...props}
          alt={alt}
          src={data.defaultAvatarUrl}
          width={parsedSize}
          height={parsedSize}
          borderRadius={size}
        />
        <Image
          alt={alt}
          ref={ref}
          src={`https://gravatar.com/avatar/${data.emailMd5}?d=blank&s=${parsedSize * 3}`}
          width={parsedSize}
          height={parsedSize}
          borderRadius={size}
          position="absolute"
          top={0}
          left={0}
        />
      </View>
    )
  }

  return (
    <UserPrimaryPhotoFrame
      {...(props as any)}
      alt={alt}
      ref={ref}
      src={data.defaultAvatarUrl}
      width={parsedSize}
      height={parsedSize}
      borderRadius={size}
    />
  )
})

export const UserDefaultPhoto = UserPrimaryPhotoFrame.styleable<
  { defaultAvatarUrl: string } & Omit<ImageProps, 'source'>
>(({ defaultAvatarUrl, ...props }, ref) => {
  return <UserPrimaryPhotoFrame {...(props as any)} ref={ref} src={defaultAvatarUrl} />
})
